import { DeleteExamDialog, ExamDialog } from '@/components/exams';
import { Listing } from '@/components/Listing';
import Page, {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderDescription,
  PageHeaderTitle,
} from '@/components/Page';
import { useCreateExam, useDeleteExam, useExams, useUpdateExam } from '@/hooks/useExams';
import { usePaginationState } from '@/hooks/usePaginationState';
import type { AppContext } from '@/main';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from '@ui/components';
import { Button } from '@ui/components/button';

import { format } from 'date-fns';
import {
  BadgeQuestionMark,
  Book,
  ContactRound,
  Edit2,
  MoreHorizontal,
  Plus,
  Trash2,
  User,
} from 'lucide-react';
import { Dispatch, SetStateAction, useState } from 'react';

export const Route = createFileRoute('/_dashboard/exams/')<AppContext>({
  component: RouteComponent,
  loader: () => ({
    crumb: 'Exams',
  }),
});

interface Exam {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  archived?: boolean;
  students?: unknown[];
  courses?: unknown[];
  questions?: unknown[];
}

function RouteComponent() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingExam, setEditingExam] = useState<Exam | null>(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null as string | null,
    name: '',
  });

  const { pagination, setPagination, orderDirection, setOrderDirection, orderBy, setOrderBy } =
    usePaginationState<'name' | 'createdAt' | 'updatedAt'>({
      defaultOrder: 'createdAt',
    });

  const {
    data: examsResponse,
    isLoading,
    error,
  } = useExams({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    orderBy,
    orderDirection,
  });
  const exams = examsResponse?.items || [];

  const table = useExamsTable({
    exams,
    setEditingExam,
    setDeleteDialog,
    pagination,
    setPagination,
    rowCount: examsResponse?.totalItems || 0,
  });

  const createExamMutation = useCreateExam('');
  const updateExamMutation = useUpdateExam();
  const deleteExamMutation = useDeleteExam();

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-destructive">Failed to load exams</div>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderContent>
          <PageHeaderTitle>Exams</PageHeaderTitle>
          <PageHeaderDescription>
            Manage your exams and assessments
            {examsResponse && <span className="ml-2">({examsResponse.totalItems} total)</span>}
          </PageHeaderDescription>
        </PageHeaderContent>
        <PageHeaderActions>
          <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Exam
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <Listing.Root
        values={exams}
        CardComponent={ExamCardComponent}
        table={table}
        isLoading={isLoading}
        totalItems={examsResponse?.totalItems || 0}
        onDelete={(item: Exam) => {
          setDeleteDialog({
            open: true,
            id: item.id,
            name: item.name,
          });
        }}
        onEdit={(item: Exam) => {
          setEditingExam(item);
        }}
      >
        <Listing.Header.Root>
          <Listing.Header.StatusFilter
            active="all"
            options={[
              {
                label: 'All Exams',
                value: 'all',
              },
            ]}
            default="all"
            onChange={() => {}}
          />
          <Listing.Header.Selector.Root>
            <Listing.Header.Selector.View />
            <Listing.Header.Selector.OrderBy
              default="name"
              onChange={(value) => {
                const [orderBy, orderDirection] = value.split('-');
                setOrderBy(orderBy as 'name' | 'createdAt' | 'updatedAt');
                setOrderDirection(orderDirection as 'asc' | 'desc');
              }}
              options={[
                {
                  label: 'Name',
                  value: 'name',
                },
                {
                  label: 'Created At',
                  value: 'createdAt',
                },
                {
                  label: 'Updated At',
                  value: 'updateAt',
                },
              ]}
              orderBy={orderBy}
              orderDirection={orderDirection}
            />
            <Listing.Header.Selector.PageSize
              value={pagination.pageSize.toString()}
              onChange={(value) => setPagination({ ...pagination, pageSize: parseInt(value, 10) })}
            />
          </Listing.Header.Selector.Root>
        </Listing.Header.Root>
      </Listing.Root>

      <ExamDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSave={async (data) => {
          await createExamMutation.mutateAsync(data);
          setIsCreateDialogOpen(false);
        }}
      />

      <ExamDialog
        open={!!editingExam}
        onOpenChange={(open) => !open && setEditingExam(null)}
        exam={editingExam}
        onSave={async (data) => {
          await updateExamMutation.mutateAsync({ examId: editingExam?.id ?? '', data });
          setEditingExam(null);
        }}
      />

      <DeleteExamDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        examId={deleteDialog.id}
        examName={deleteDialog.name}
        onDelete={async (examId) => {
          await deleteExamMutation.mutateAsync(examId);
        }}
      />
    </Page>
  );
}

function ExamCardComponent({
  item,
  onDelete,
  onEdit,
}: {
  item: Exam;
  onDelete: (item: Exam) => void;
  onEdit: (item: Exam) => void;
}) {
  return (
    <Item
      key={item.id}
      variant="outline"
      className="bg-card text-card-foreground rounded-sm border py-6 shadow-sm hover:shadow-lg transition-shadow gap-2 pt-6"
    >
      <ItemMedia>
        <div className="bg-primary/10 text-primary rounded w-12 h-12 flex items-center justify-center">
          <User />
        </div>
      </ItemMedia>
      <ItemContent>
        <ItemTitle>
          <Link
            to={`/exams/$examId`}
            params={{
              examId: item.id,
            }}
          >
            {item.name}
          </Link>
        </ItemTitle>
      </ItemContent>
      <ItemActions>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(item)}>
              <Edit2 className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(item)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </ItemActions>
      <ItemContent>
        <div className="flex gap-2 justify-between">
          <div className="flex flex-col items-center text-center justify-center p-4 rounded bg-background text-sm text-foreground gap-2 border border-dashed">
            <Book className="w-6 h-6" />
            {item.courses?.length || 0} Courses
          </div>
          <div className="flex flex-col items-center text-center justify-center p-4 rounded bg-background text-sm text-foreground gap-2 border border-dashed">
            <BadgeQuestionMark className="w-6 h-6" />
            {item.questions?.length || 0} Questions
          </div>
          <div className="flex flex-col items-center text-center justify-center p-4 rounded bg-background text-sm text-foreground gap-2 border border-dashed">
            <ContactRound className="w-6 h-6" />
            {item.students?.length || 0} Students
          </div>
        </div>
      </ItemContent>
    </Item>
  );
}

function useExamsTable({
  exams,
  setEditingExam,
  setDeleteDialog,
  pagination,
  setPagination,
  rowCount,
}: {
  exams: Exam[];
  setEditingExam: (exam: Exam) => void;
  setDeleteDialog: (params: { open: boolean; id: string | null; name: string }) => void;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  rowCount: number;
}) {
  return useReactTable({
    data: exams,
    columns: [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row: { original: exam } }) => (
          <Link
            to="/exams/$examId"
            params={{ examId: exam.id }}
            className="font-medium hover:underline"
          >
            {exam.name}
          </Link>
        ),
      },
      {
        accessorKey: 'courses',
        header: 'Courses',
        cell: ({ row: { original: exam } }) => {
          return (
            <Badge variant="secondary" className="font-mono">
              {exam.courses?.length ?? '0'}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'archived',
        header: 'Status',
        cell: ({
          row: {
            original: { archived },
          },
        }) => (
          <Badge variant={archived ? 'outline' : 'default'}>
            {archived ? 'Archived' : 'Active'}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created at',
        cell: ({ row: { original: exam } }) => (
          <div className="text-muted-foreground">
            {format(new Date(exam.createdAt), 'MMM dd, yyyy')}
          </div>
        ),
      },
      {
        id: 'actions',
        enableHiding: false,
        header: 'Actions',
        cell: ({ row: { original: exam } }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setEditingExam(exam);
                }}
              >
                <Edit2 className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  if (exam) {
                    setDeleteDialog({
                      open: true,
                      id: exam.id,
                      name: exam.name,
                    });
                  }
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    onColumnFiltersChange: () => {},
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
    manualPagination: true,
    rowCount,
  });
}
